/**
 * Sprint 29 M0 — Operational Intent server functions.
 *
 * proposeIntent: AI translates operator prompt -> OperationalIntent candidate.
 *                AI never writes to the database.
 * activateIntent: atomic upsert of N rules under one intent_id via
 *                 activate_hold_rule_intent RPC. Validate-then-write.
 * discardIntent: telemetry-only no-op (proposals are transient).
 *
 * See docs/product/create-policy-vision.md for principles.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  HOLD_REASONS, TRIGGERS, previewHoldRule, runHoldRulePreview,
  validateRuleParams,
  type HoldReason, type RuleScope,
} from "./hold-rules.functions";
import type {
  OperationalIntent, ProposedHoldRule, ComposedPreview, ScopeConflict, IntentScope, SampleMatch,
  UnsupportedCondition,
  HoldCreationIntent, ProposedCreationRule, CreationComposedPreview,
} from "./intents-types";
import { HOLD_CREATION_DESCRIPTOR } from "./capabilities/hold-creation/descriptor";
import { APPROVER_ROLES } from "./capabilities/hold-creation/actions";
import {
  IRProposalZ, compileIRProposal, buildHoldCreationIRSchema,
  IRCompileError, normalizeHCEnvelope, groundHCEnvelopeFromPrompt, type CompiledProposal,
} from "./capabilities/hold-creation/ir";
import { ORDER_FIELDS, resolveSupportedFields, evaluate, buildOrderContext } from "./policy-conditions";
import { PipelineRun, recordOutcome } from "./policy-compiler/pipeline";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SbLike = any;

const LOVABLE_AI_URL = "https://ai.gateway.lovable.dev/v1/chat/completions";
const DEFAULT_MODEL = "google/gemini-3-flash-preview";

const ParamValueZ = z.union([z.number(), z.string(), z.boolean(), z.null()]);
const ProposedRuleZ = z.object({
  hold_reason: z.enum(HOLD_REASONS),
  trigger: z.enum(TRIGGERS),
  params: z.record(z.string(), ParamValueZ).default({}),
  is_active: z.boolean().default(true),
  rationale: z.string().max(500).default(""),
}).superRefine((val, ctx) => {
  // Per-trigger params shape — same validator the manual editor uses.
  try {
    validateRuleParams(val.trigger, val.params);
  } catch (e) {
    ctx.addIssue({ code: "custom", path: ["params"], message: (e as Error).message });
  }
});

const UnsupportedConditionZ = z.object({
  kind: z.enum(["hold_creation_predicate", "unsupported_predicate", "threshold_dropped"]),
  detail: z.string().min(1).max(280),
});

const IntentEnvelopeZ = z.object({
  title: z.string().min(1).max(120),
  summary: z.string().min(1).max(600),
  proposals: z.array(ProposedRuleZ).max(6).default([]),
  assumptions: z.array(z.string().max(280)).max(8).default([]),
  rejected: z.array(z.string().max(280)).max(8).default([]),
  unsupported_conditions: z.array(UnsupportedConditionZ).max(8).default([]),
});

/** Operator-supplied scope. AI never infers scope. */
const IntentScopeInputZ = z.discriminatedUnion("level", [
  z.object({ level: z.literal("tenant"), customer_id: z.null() }),
  z.object({ level: z.literal("customer"), customer_id: z.string().uuid() }),
]);

function scopeInputToRule(s: z.infer<typeof IntentScopeInputZ>): RuleScope {
  return s.level === "tenant"
    ? { scope_level: "tenant", customer_id: null }
    : { scope_level: "customer", customer_id: s.customer_id };
}

/* ── proposeIntent ─────────────────────────────────────────────────────── */

export const proposeIntent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      tenantId: z.string().uuid(),
      prompt: z.string().min(4).max(2000),
      scope: IntentScopeInputZ,
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<OperationalIntent> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) {
      throw new Error("LOVABLE_API_KEY is not configured — enable Lovable AI for this project.");
    }

    // Resolve scope. Customer must belong to tenant; RLS would also catch
    // it, but we want a clean error and a label for the UI.
    let customer_label: string | null = null;
    if (data.scope.level === "customer") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = context.supabase as any;
      const { data: cust, error: cErr } = await sb
        .from("customers")
        .select("id, name")
        .eq("id", data.scope.customer_id)
        .eq("tenant_id", data.tenantId)
        .maybeSingle();
      if (cErr) throw new Error(`E_SCOPE: ${cErr.message}`);
      if (!cust) throw new Error("Customer not found in this tenant.");
      customer_label = cust.name as string;
    }
    const ruleScope = scopeInputToRule(data.scope);

    const systemPrompt = [
      "You are CairnOS's Operational Intent compiler for warehouse Hold Release Rules.",
      "Translate an operator's plain-English intent into one or more deterministic hold rules.",
      "You do NOT execute anything. You propose; a human approves.",
      "",
      "CAPABILITY BOUNDARY (read carefully):",
      "- This surface governs HOW EXISTING HOLDS RELEASE. It does NOT decide when an",
      "  order should be PLACED on hold. Order-hold creation is a separate capability",
      "  that does not exist yet.",
      "- The supported triggers cannot express any of: order monetary value, line",
      "  totals, SKU, product tag, channel, weight, geography, customer tier, or",
      "  any other order/customer attribute. Only the listed trigger params are valid.",
      "",
      "If the operator's prompt asks for hold PLACEMENT (e.g. 'place orders over",
      "$1,000 on manual hold', 'require approval for international orders', 'flag",
      "orders containing SKU X'), you MUST:",
      "  1. List each such condition in `unsupported_conditions` with",
      "     kind='hold_creation_predicate' and a one-line detail quoting the",
      "     unsupported part of the prompt.",
      "  2. Do NOT fabricate a release rule that pretends to capture the placement",
      "     intent. If only the placement intent is present, return ZERO proposals.",
      "  3. If the prompt mixes a release intent with an unsupported predicate (e.g.",
      "     'auto-release credit holds after 30 min for orders under $500'), emit the",
      "     release rule WITHOUT the unsupported predicate AND list the dropped",
      "     predicate in `unsupported_conditions` with kind='unsupported_predicate'",
      "     or 'threshold_dropped' as appropriate.",
      "",
      "Each proposed rule MUST use:",
      `- hold_reason: one of ${HOLD_REASONS.join(", ")}`,
      `- trigger: one of ${TRIGGERS.join(", ")}`,
      "- params:",
      "    time_elapsed        -> { after_minutes: integer >= 1 }",
      "    fraud_score_below   -> { threshold: number 0..1 }",
      "    credit_recovered    -> {}",
      "    manual_only         -> {}",
      "",
      "Hard rules:",
      "- One rule per hold_reason. If the intent implies multiple reasons, emit one rule per reason.",
      "- Refuse (return zero proposals and list under rejected) anything that is not expressible as a hold-release rule.",
      "- Never invent reasons, triggers, or param keys outside the lists above.",
      "- title: short imperative (<=80 chars). summary: one operator-readable paragraph.",
      "- rationale per proposal: one sentence explaining why this rule advances the intent.",
      "- assumptions: list any defaults you applied (e.g. 'assumed 30 minute wait').",
      "- No confidence scores. No probabilities. Be deterministic.",
      "",
      "Scope was selected by the operator outside this prompt — do not",
      "discuss tenant vs customer scope, and do not propose rules that",
      "would only make sense at a different scope than the operator chose.",
    ].join("\n");

    const resp = await fetch(LOVABLE_AI_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: DEFAULT_MODEL,
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: data.prompt },
        ],
        tools: [{
          type: "function",
          function: {
            name: "submit_operational_intent",
            description: "Submit one OperationalIntent that compiles into hold_release_rules.",
            parameters: {
              type: "object",
              properties: {
                title: { type: "string" },
                summary: { type: "string" },
                proposals: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      hold_reason: { type: "string", enum: [...HOLD_REASONS] },
                      trigger: { type: "string", enum: [...TRIGGERS] },
                      params: { type: "object" },
                      is_active: { type: "boolean" },
                      rationale: { type: "string" },
                    },
                    required: ["hold_reason", "trigger", "params", "rationale"],
                  },
                },
                assumptions: { type: "array", items: { type: "string" } },
                rejected: { type: "array", items: { type: "string" } },
                unsupported_conditions: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      kind: {
                        type: "string",
                        enum: ["hold_creation_predicate", "unsupported_predicate", "threshold_dropped"],
                      },
                      detail: { type: "string" },
                    },
                    required: ["kind", "detail"],
                  },
                },
              },
              required: ["title", "summary", "proposals", "unsupported_conditions"],
            },
          },
        }],
        tool_choice: { type: "function", function: { name: "submit_operational_intent" } },
      }),
    });

    if (resp.status === 429) throw new Error("AI gateway rate limit reached — try again in a moment.");
    if (resp.status === 402) throw new Error("Lovable AI credits exhausted — add funds in workspace settings.");
    if (!resp.ok) throw new Error(`AI gateway error: ${resp.status}`);
    const body = await resp.json();

    const toolCall = body?.choices?.[0]?.message?.tool_calls?.[0];
    if (!toolCall?.function?.arguments) {
      throw new Error("AI returned no proposal. Try rephrasing your intent.");
    }
    let parsed: z.infer<typeof IntentEnvelopeZ>;
    try {
      parsed = IntentEnvelopeZ.parse(JSON.parse(toolCall.function.arguments));
    } catch (err) {
      throw new Error(`AI proposal failed schema validation: ${(err as Error).message}`);
    }

    // Deduplicate by hold_reason — one rule per reason is the canonical constraint.
    const byReason = new Map<HoldReason, ProposedHoldRule>();
    for (const p of parsed.proposals) byReason.set(p.hold_reason, p);
    const proposals = Array.from(byReason.values());

    // Heuristic backstop: even if the AI omitted `unsupported_conditions`,
    // catch the highest-risk silent drops by scanning the prompt. We err on
    // the side of flagging — operator can re-prompt if a flag is spurious,
    // but a missed drop activates a wrong rule.
    const unsupported_conditions: UnsupportedCondition[] = [
      ...(parsed.unsupported_conditions ?? []),
      ...detectUnrepresentedConditions(data.prompt, proposals),
    ];
    // De-duplicate by detail string.
    const seenDetail = new Set<string>();
    const uniqueUnsupported = unsupported_conditions.filter((u) => {
      const k = `${u.kind}:${u.detail.toLowerCase()}`;
      if (seenDetail.has(k)) return false;
      seenDetail.add(k);
      return true;
    });

    const composed_preview = await composePreview(
      context.supabase as unknown as SbLike,
      data.tenantId,
      ruleScope,
      proposals,
    );

    // Telemetry — app_logs, no new tables.
    await context.supabase.from("app_logs").insert({
      level: "info",
      source: "intents",
      message: "intent.proposed",
      extras: {
        tenant_id: data.tenantId,
        proposal_count: proposals.length,
        reasons: proposals.map((p) => p.hold_reason),
        prompt_chars: data.prompt.length,
        scope_level: ruleScope.scope_level,
        customer_id: ruleScope.customer_id,
        unsupported_count: uniqueUnsupported.length,
        unsupported_kinds: uniqueUnsupported.map((u) => u.kind),
      },
    } as never);

    const scopeOut: IntentScope =
      ruleScope.scope_level === "tenant"
        ? { level: "tenant", customer_id: null }
        : { level: "customer", customer_id: ruleScope.customer_id };

    return {
      intent_id: crypto.randomUUID(),
      title: parsed.title.slice(0, 120),
      summary: parsed.summary.slice(0, 600),
      prompt: data.prompt,
      scope: scopeOut,
      customer_label,
      proposals,
      composed_preview,
      assumptions: parsed.assumptions,
      rejected: parsed.rejected,
      unsupported_conditions: uniqueUnsupported,
    };
  });

/* ── activateIntent ────────────────────────────────────────────────────── */

const ActivateZ = z.object({
  tenantId: z.string().uuid(),
  intentId: z.string().uuid(),
  prompt: z.string().min(1).max(2000),
  summary: z.string().min(1).max(600),
  source: z.enum(["ai_proposal", "ai_proposal_modified"]),
  proposals: z.array(ProposedRuleZ).min(1).max(6),
  scope: IntentScopeInputZ,
  unsupported_conditions: z.array(UnsupportedConditionZ).default([]),
});

export const activateIntent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ActivateZ.parse(d))
  .handler(async ({ data, context }) => {
    // Hard gate: a material condition from the prompt is unrepresented in
    // the proposed policy. Per Sprint 29 acceptance criteria, no AI-generated
    // rule may activate when this is the case. Operator must re-prompt or
    // wait for the missing capability (hold creation rules).
    if (data.unsupported_conditions.length > 0) {
      const detail = data.unsupported_conditions
        .map((u) => `[${u.kind}] ${u.detail}`)
        .join("; ");
      throw new Error(
        `E_CAPABILITY_GAP: This intent cannot be activated — ${data.unsupported_conditions.length} condition(s) from your prompt are not representable by Hold Release Rules: ${detail}`,
      );
    }
    // Up-front validation: reject duplicate reasons before opening the tx.
    const seen = new Set<HoldReason>();
    for (const p of data.proposals) {
      if (seen.has(p.hold_reason)) {
        throw new Error(`Duplicate proposal for hold reason "${p.hold_reason}" — one rule per reason.`);
      }
      seen.add(p.hold_reason);
      // Defense-in-depth: ProposedRuleZ already ran this, but call it again
      // so any future caller of activate without going through propose still
      // gets canonical per-trigger validation before we hit the RPC.
      validateRuleParams(p.trigger, p.params);
    }
    const ruleScope = scopeInputToRule(data.scope);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = context.supabase as any;
    const { data: count, error } = await sb.rpc(
      "activate_hold_rule_intent",
      {
        p_tenant_id: data.tenantId,
        p_intent_id: data.intentId,
        p_intent_prompt: data.prompt,
        p_intent_summary: data.summary,
        p_source: data.source,
        p_scope_level: ruleScope.scope_level,
        p_customer_id: ruleScope.customer_id,
        p_rules: data.proposals.map((p) => ({
          hold_reason: p.hold_reason,
          trigger: p.trigger,
          params: p.params,
          is_active: p.is_active,
        })),
      } as never,
    );
    if (error) throw new Error(`E_ACTIVATE: ${error.message}`);

    await context.supabase.from("app_logs").insert({
      level: "info",
      source: "intents",
      message: "intent.activated",
      extras: {
        tenant_id: data.tenantId,
        intent_id: data.intentId,
        source: data.source,
        scope_level: ruleScope.scope_level,
        customer_id: ruleScope.customer_id,
        rules_written: count ?? data.proposals.length,
      },
    } as never);

    return { ok: true, rulesWritten: (count as number | null) ?? data.proposals.length };
  });

/* ── discardIntent ─────────────────────────────────────────────────────── */

export const discardIntent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      tenantId: z.string().uuid(),
      intentId: z.string().uuid(),
      reason: z.string().max(280).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await context.supabase.from("app_logs").insert({
      level: "info",
      source: "intents",
      message: "intent.discarded",
      extras: {
        tenant_id: data.tenantId,
        intent_id: data.intentId,
        reason: data.reason ?? null,
      },
    } as never);
    await recordOutcome(context.supabase, data.intentId, "discarded", { reason: data.reason ?? null });
    return { ok: true };
  });

/* ── composePreview ────────────────────────────────────────────────────────
 * Calls the SHARED `runHoldRulePreview` per proposal and composes a single
 * intent-level view. No UI-side policy logic; the matching universe comes
 * from the same helper the manual rule editor uses, so preview and
 * activation are guaranteed to interpret scope identically.
 * ----------------------------------------------------------------------- */
async function composePreview(
  supabase: { rpc: unknown } & Record<string, unknown>,
  tenantId: string,
  scope: RuleScope,
  proposals: ProposedHoldRule[],
): Promise<ComposedPreview> {
  let matching_now = 0;
  let matching_historical_90d = 0;
  const examples: ComposedPreview["examples"] = [];
  const footnotes: string[] = [];
  const scope_conflicts: ScopeConflict[] = [];
  const sample_matches: SampleMatch[] = [];

  for (const p of proposals) {
    const slice = await runHoldRulePreview(supabase, {
      tenantId,
      holdReason: p.hold_reason,
      trigger: p.trigger,
      params: p.params,
      scope,
    });
    matching_now += slice.matchingNow;
    matching_historical_90d += slice.matchingHistorical;
    for (const ex of slice.examples) {
      if (examples.length < 5) examples.push(ex);
    }
    // Remember the first sample for the matching-rule callout below.
    // We resolve `governs` after we've loaded existing rules.
    sample_matches.push({
      hold_reason: p.hold_reason,
      governs: "this",
      sample_label: slice.examples[0]?.label ?? null,
      detail: "",
    });
  }

  // Scope-conflict detection — read existing rules for the reasons in play.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any;
  const reasons = proposals.map((p) => p.hold_reason);
  const { data: existing } = await sb
    .from("hold_release_rules")
    .select("hold_reason, trigger, scope_level, customer_id")
    .eq("tenant_id", tenantId)
    .in("hold_reason", reasons);
  for (const p of proposals) {
    const tenantRule = (existing ?? []).find(
      (r: { hold_reason: string; scope_level: string }) =>
        r.hold_reason === p.hold_reason && r.scope_level === "tenant",
    );
    if (scope.scope_level === "tenant" && tenantRule) {
      scope_conflicts.push({
        kind: "replaces_tenant_rule",
        hold_reason: p.hold_reason,
        existing_trigger: tenantRule.trigger as string,
      });
    } else if (scope.scope_level === "customer") {
      if (tenantRule) {
        scope_conflicts.push({
          kind: "specializes_tenant_rule",
          hold_reason: p.hold_reason,
          tenant_trigger: tenantRule.trigger as string,
        });
      }
      const customerRule = (existing ?? []).find(
        (r: { hold_reason: string; scope_level: string; customer_id: string | null }) =>
          r.hold_reason === p.hold_reason &&
          r.scope_level === "customer" &&
          r.customer_id === scope.customer_id,
      );
      if (customerRule) {
        scope_conflicts.push({
          kind: "replaces_customer_rule",
          hold_reason: p.hold_reason,
          existing_trigger: customerRule.trigger as string,
        });
      }
    }
    // Resolve the sample-match governance for this proposal.
    const sm = sample_matches.find((s) => s.hold_reason === p.hold_reason)!;
    if (!sm.sample_label) {
      sm.governs = "none";
      sm.detail = `No open ${p.hold_reason} hold in scope to sample.`;
    } else if (scope.scope_level === "customer") {
      // Customer scope ALWAYS wins for that customer's holds.
      sm.governs = "this";
      sm.detail = tenantRule
        ? `This customer rule will govern ${sm.sample_label} (overrides the tenant '${tenantRule.trigger}' rule).`
        : `This customer rule will govern ${sm.sample_label} (no tenant rule for this reason).`;
    } else {
      // Tenant scope: governs every open hold of this reason in tenant
      // EXCEPT those whose customer has a customer-specific rule. The
      // representative example is a tenant-wide sample; calling out that
      // some customers may keep their own rule is enough operator signal.
      sm.governs = "this";
      const customerOverrides = (existing ?? []).filter(
        (r: { hold_reason: string; scope_level: string }) =>
          r.hold_reason === p.hold_reason && r.scope_level === "customer",
      ).length;
      sm.detail = customerOverrides > 0
        ? `This tenant rule will govern ${sm.sample_label}. ${customerOverrides} customer-specific rule(s) for this reason will continue to override for those customers.`
        : `This tenant rule will govern ${sm.sample_label}.`;
    }
  }

  footnotes.push(
    scope.scope_level === "tenant"
      ? "Counts cover all open holds (now) and the last 90 days (historical) across this tenant for the reasons this intent governs."
      : "Counts cover open holds (now) and last 90 days (historical) for the selected customer only.",
    "Activating the intent will not change which orders are on hold — only how they release.",
  );

  return { matching_now, matching_historical_90d, examples, footnotes, scope_conflicts, sample_matches };
}

// previewHoldRule is re-exported so the UI can pull a single-rule preview in
// Advanced view without a second import path.
export { previewHoldRule };

/* ── Heuristic backstop for capability-gap detection ─────────────────────
 * Scans the operator's prompt for phrasing that almost always indicates a
 * material condition the Hold Release Rules surface cannot represent.
 * Conservative on purpose — false positives are recoverable (operator
 * re-prompts), false negatives ship a wrong rule.
 * --------------------------------------------------------------------- */
function detectUnrepresentedConditions(
  prompt: string,
  proposals: ProposedHoldRule[],
): UnsupportedCondition[] {
  const out: UnsupportedCondition[] = [];
  const p = prompt.toLowerCase();

  // 1. Hold-PLACEMENT intent — current surface only governs release.
  const placementPhrases = [
    "place on hold", "place orders on hold", "put on hold", "put orders on hold",
    "hold orders", "flag orders", "require approval", "require manual approval",
    "block orders", "block the order", "needs approval", "must be approved",
  ];
  if (placementPhrases.some((ph) => p.includes(ph))) {
    out.push({
      kind: "hold_creation_predicate",
      detail:
        "Prompt asks to PLACE orders on hold based on a condition. This surface only governs how existing holds release — hold-placement rules are a separate capability that does not exist yet.",
    });
  }

  // 2. Dollar threshold ($1,000, $1k, over 500 dollars, etc.) — never
  //    expressible in any current trigger params.
  const dollarMatch =
    prompt.match(/\$\s?\d[\d,]*(?:\.\d+)?(?:\s?[kKmM])?/) ||
    prompt.match(/\b\d[\d,]*(?:\.\d+)?\s?(dollars?|usd)\b/i);
  if (dollarMatch) {
    out.push({
      kind: "threshold_dropped",
      detail: `Prompt references a monetary threshold ("${dollarMatch[0].trim()}"). Hold rule triggers cannot evaluate order value.`,
    });
  }

  // 3. SKU / tag / channel / weight / geography filters.
  const filterTerms: Array<[RegExp, string]> = [
    [/\bsku\b/i, "SKU"],
    [/\btag(s|ged)?\b/i, "tag"],
    [/\bchannel\b/i, "channel"],
    [/\bweight\b/i, "weight"],
    [/\b(international|domestic|country|region|state|zip|postal)\b/i, "geography"],
    [/\b(vip|tier|wholesale|retail) customer/i, "customer tier"],
  ];
  for (const [re, label] of filterTerms) {
    if (re.test(prompt)) {
      out.push({
        kind: "unsupported_predicate",
        detail: `Prompt references a ${label} filter, which Hold Release Rules cannot evaluate.`,
      });
    }
  }

  // 4. If we found a placement intent but the AI also produced proposals,
  //    those proposals are necessarily a partial/wrong capture — keep both
  //    signals so the UI shows the gap regardless of what was emitted.
  void proposals;
  return out;
}

/* ═════════════════════════════════════════════════════════════════════════
 * Sprint 29 M2 — M1: Hold Creation compiler.
 *
 * Reference implementation for the descriptor-driven compiler pattern.
 * AI may ONLY emit:
 *   - condition fields drawn from the canonical registry minus the
 *     descriptor's excludedFields (Canonical Policy Principle),
 *   - operators from descriptor.allowedOperators,
 *   - actions from descriptor.actions with their typed params.
 *
 * The same descriptor governs runtime evaluation and Preview Impact, so
 * a proposal that compiles here is by construction executable.
 * ════════════════════════════════════════════════════════════════════════ */

const HC_FIELDS = resolveSupportedFields(HOLD_CREATION_DESCRIPTOR, ORDER_FIELDS);

// IR envelope — AI emits flat predicates + actions; backend compiles.
const HCEnvelopeZ = z.object({
  title: z.string().min(1).max(120),
  summary: z.string().min(1).max(600),
  proposals: z.array(IRProposalZ).max(6).default([]),
  assumptions: z.array(z.string().max(280)).max(8).default([]),
  rejected: z.array(z.string().max(280)).max(8).default([]),
  unsupported_conditions: z.array(UnsupportedConditionZ).max(8).default([]),
});

function buildHoldCreationToolSchema() {
  // Per-action params shape (oneOf on kind so the model emits the right keys).
  const placeHold = {
    type: "object",
    properties: {
      kind:   { type: "string", enum: ["place_hold"] },
      params: {
        type: "object",
        properties: {
          reason: { type: "string", enum: [...HOLD_REASONS] },
          note:   { type: "string" },
        },
        required: ["reason"],
      },
    },
    required: ["kind", "params"],
  };
  const requireApproval = {
    type: "object",
    properties: {
      kind:   { type: "string", enum: ["require_approval"] },
      params: {
        type: "object",
        properties: {
          approver_role: { type: "string", enum: [...APPROVER_ROLES] },
          reason:        { type: "string", enum: [...HOLD_REASONS] },
        },
        required: ["approver_role", "reason"],
      },
    },
    required: ["kind", "params"],
  };
  const assignCategory = {
    type: "object",
    properties: {
      kind:   { type: "string", enum: ["assign_hold_category"] },
      params: {
        type: "object",
        properties: {
          category: { type: "string" },
          reason:   { type: "string", enum: [...HOLD_REASONS] },
        },
        required: ["category", "reason"],
      },
    },
    required: ["kind", "params"],
  };

  return buildHoldCreationIRSchema({ placeHold, requireApproval, assignCategory });
}

function buildHoldCreationSystemPrompt(): string {
  const fieldLines = HC_FIELDS.map(
    (f) => `  - ${f.path} (${f.kind}, ops: ${f.operators.join("/")})${f.description ? ` — ${f.description}` : ""}`,
  ).join("\n");
  const actionLines = HOLD_CREATION_DESCRIPTOR.actions
    .map((a) => `  - ${a.kind} — ${a.paramsSummary}`)
    .join("\n");
  return [
    "You are CairnOS's Operational Intent compiler for the Hold Creation capability.",
    "Translate an operator's plain-English intent into deterministic policies that decide WHEN to PLACE an order on hold at import time.",
    "You do NOT execute anything. You propose; a human approves.",
    "",
    `CAPABILITY: ${HOLD_CREATION_DESCRIPTOR.label}`,
    HOLD_CREATION_DESCRIPTOR.scopeNotes,
    "",
    "ALLOWED CONDITION FIELDS (left-hand sides of leaves):",
    fieldLines,
    "",
    "ALLOWED ACTIONS:",
    actionLines,
    "",
    "Rules:",
    "- For each proposal, emit a FLAT list of predicates and a single combinator.",
    "  Each predicate is { field, op, value }. Do NOT construct nested trees;",
    "  the backend builds the canonical condition tree from your predicates.",
    "- Use ONLY the field paths, operators, and action kinds listed above.",
    "- combinator defaults to 'and'. Use 'or' when the intent says \"any of\".",
    "- If the operator's intent requires a fact not in the field list, list it under unsupported_conditions",
    "  with kind='unsupported_predicate' instead of inventing a field.",
    "- Emit one proposal per distinct outcome. Multi-outcome intents become multiple proposals.",
    "- Provide a one-sentence rationale per proposal.",
    "- No confidence scores. No probabilities. Be deterministic.",
    "- Scope (tenant vs customer) is selected by the operator outside this prompt — do not discuss it.",
    "",
    "EXAMPLE — operator prompt: \"Require manager approval for orders over $1,000\"",
    "Tool call arguments:",
    "{",
    "  \"title\": \"Manager approval for high-value orders\",",
    "  \"summary\": \"Hold any order whose subtotal exceeds $1,000 pending manager approval.\",",
    "  \"proposals\": [{",
    "    \"name\": \"Approve orders over $1,000\",",
    "    \"description\": \"Require manager approval when subtotal > 1000.\",",
    "    \"combinator\": \"and\",",
    "    \"predicates\": [{ \"field\": \"order.subtotal\", \"op\": \"gt\", \"value\": 1000 }],",
    "    \"actions\": [{ \"kind\": \"require_approval\", \"params\": { \"approver_role\": \"manager\", \"reason\": \"manual\" } }],",
    "    \"priority\": 100,",
    "    \"is_active\": true,",
    "    \"rationale\": \"Captures the $1,000 threshold and the manager-approval requirement.\"",
    "  }],",
    "  \"assumptions\": [\"Subtotal interpreted as order.subtotal in tenant currency.\"],",
    "  \"rejected\": [],",
    "  \"unsupported_conditions\": []",
    "}",
  ].join("\n");
}

/* ── proposeHoldCreationIntent ───────────────────────────────────────── */

export const proposeHoldCreationIntent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      tenantId: z.string().uuid(),
      prompt: z.string().min(4).max(2000),
      scope: IntentScopeInputZ,
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<HoldCreationIntent> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured — enable Lovable AI for this project.");

    const intentId = crypto.randomUUID();
    const run = new PipelineRun({
      intentId,
      tenantId: data.tenantId,
      userId: context.userId ?? null,
      capability: HOLD_CREATION_DESCRIPTOR.capability,
      descriptorVersion: HOLD_CREATION_DESCRIPTOR.version,
      provider: "lovable_ai_gateway",
      model: DEFAULT_MODEL,
      prompt: data.prompt,
    });

    let customer_label: string | null = null;
    if (data.scope.level === "customer") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = context.supabase as any;
      const { data: cust, error: cErr } = await sb
        .from("customers").select("id, name")
        .eq("id", data.scope.customer_id).eq("tenant_id", data.tenantId)
        .maybeSingle();
      if (cErr) throw new Error(`E_SCOPE: ${cErr.message}`);
      if (!cust) throw new Error("Customer not found in this tenant.");
      customer_label = cust.name as string;
    }
    const ruleScope = scopeInputToRule(data.scope);

    const resp = await fetch(LOVABLE_AI_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: DEFAULT_MODEL,
        messages: [
          { role: "system", content: buildHoldCreationSystemPrompt() },
          { role: "user",   content: data.prompt },
        ],
        tools: [{
          type: "function",
          function: {
            name: "submit_hold_creation_intent",
            description: "Submit an OperationalIntent that compiles into hold_creation_rules.",
            parameters: buildHoldCreationToolSchema(),
          },
        }],
        tool_choice: { type: "function", function: { name: "submit_hold_creation_intent" } },
      }),
    });
    if (!resp.ok) {
      const code = resp.status === 429 ? "AI_RATE_LIMIT"
                 : resp.status === 402 ? "AI_CREDITS_EXHAUSTED"
                 : "AI_GATEWAY_ERROR";
      const friendly = resp.status === 429 ? "AI gateway rate limit reached — try again in a moment."
                     : resp.status === 402 ? "Lovable AI credits exhausted — add funds in workspace settings."
                     : `AI gateway error: ${resp.status}`;
      run.fail("intent_extracted", "ai_unavailable", { code, message: friendly });
      await run.persist(context.supabase);
      throw new Error(friendly);
    }
    const body = await resp.json();

    const toolCall = body?.choices?.[0]?.message?.tool_calls?.[0];
    if (!toolCall?.function?.arguments) {
      run.fail("intent_extracted", "ai_no_proposal", {
        code: "AI_NO_PROPOSAL",
        message: "AI returned no tool call.",
      });
      await run.persist(context.supabase);
      throw new Error("AI returned no proposal. Try rephrasing your intent.");
    }
    run.advance("intent_extracted", { tool: toolCall.function?.name });

    const rawArgs = toolCall.function.arguments as string;
    let parsed: z.infer<typeof HCEnvelopeZ>;
    let normalizationNotes: ReturnType<typeof normalizeHCEnvelope>["notes"] = [];
    let intentGroundingNotes: ReturnType<typeof groundHCEnvelopeFromPrompt>["notes"] = [];
    try {
      const rawParsed = JSON.parse(rawArgs);
      const { envelope: normalized, notes } = normalizeHCEnvelope(rawParsed);
      const { envelope: grounded, notes: groundingNotes } = groundHCEnvelopeFromPrompt(normalized, data.prompt);
      normalizationNotes = notes;
      intentGroundingNotes = groundingNotes;
      parsed = HCEnvelopeZ.parse(grounded);
    } catch (err) {
      // Log full internal cause; surface a friendly message to the operator.
      await context.supabase.from("app_logs").insert({
        level: "error",
        source: "intents",
        message: "hold_creation.ai_schema_invalid",
        extras: {
          tenant_id: data.tenantId,
          prompt: data.prompt,
          error: (err as Error).message,
          raw_args: rawArgs.slice(0, 4000),
        },
      } as never);
      run.setIR({ raw_args: rawArgs.slice(0, 4000) });
      run.fail("ir_validated", "schema_invalid", {
        code: "IR_SCHEMA_INVALID",
        message: "AI proposal failed IR envelope validation.",
        internal: JSON.stringify({
          error: (err as Error).message,
          normalization_notes: normalizationNotes,
            intent_grounding_notes: intentGroundingNotes,
        }),
      });
      await run.persist(context.supabase);
      // Surface a more diagnostic operator-facing message that names the
      // missing/invalid field when possible, instead of the generic
      // translation error. Detailed cause is already in policy_compiler_runs.
      let friendly = "I couldn't translate your request into a valid policy. Please try rewording it or edit the generated policy manually.";
      if (err instanceof z.ZodError && err.issues.length > 0) {
        const issue = err.issues[0];
        const path = issue.path.join(".") || "(root)";
        friendly = `I couldn't translate your request into a valid policy: ${path} — ${issue.message}. Please try rewording it or edit the generated policy manually.`;
      }
      throw new Error(friendly);
    }
    run.setIR(parsed);
    run.advance("ir_validated", {
      proposal_count: parsed.proposals.length,
      normalization_notes: normalizationNotes,
      intent_grounding_notes: intentGroundingNotes,
    });
    run.advance("descriptor_validated", {
      capability: HOLD_CREATION_DESCRIPTOR.capability,
      descriptor_version: HOLD_CREATION_DESCRIPTOR.version,
    });

    // Backend deterministically compiles the flat IR into canonical
    // condition_tree + actions[]. The AI never built an AST.
    let compiled: CompiledProposal[];
    try {
      compiled = parsed.proposals.map((ir) => compileIRProposal(ir));
    } catch (err) {
      const internal = err instanceof IRCompileError ? err.internal : (err as Error).message;
      await context.supabase.from("app_logs").insert({
        level: "error",
        source: "intents",
        message: "hold_creation.ir_compile_failed",
        extras: {
          tenant_id: data.tenantId,
          prompt: data.prompt,
          error: internal,
          raw_args: rawArgs.slice(0, 4000),
        },
      } as never);
      const friendly = err instanceof IRCompileError
        ? err.message
        : "I couldn't translate your request into a valid policy. Please try rewording it or edit the generated policy manually.";
      run.fail("canonical_compiled", "compile_failed", {
        code: "IR_COMPILE_FAILED",
        message: friendly,
        internal,
      });
      await run.persist(context.supabase);
      throw new Error(friendly);
    }

    const proposals: ProposedCreationRule[] = compiled.map((c) => ({
      name: c.name,
      description: c.description,
      condition_tree: c.condition_tree,
      actions: c.actions,
      priority: c.priority,
      is_active: c.is_active,
      rationale: c.rationale,
    }));
    run.setCanonical({ proposals });
    run.advance("canonical_compiled", { proposal_count: proposals.length });

    const composed_preview = await composeHoldCreationPreview(
      context.supabase as unknown as SbLike,
      data.tenantId,
      ruleScope,
      proposals,
    );
    run.advance("preview_generated", {
      per_rule_counts: composed_preview.per_rule.map((r) => r.matching_in_sample),
    });
    run.advance("awaiting_approval");
    await run.persist(context.supabase);

    await context.supabase.from("app_logs").insert({
      level: "info",
      source: "intents",
      message: "hold_creation.proposed",
      extras: {
        tenant_id: data.tenantId,
        intent_id: intentId,
        proposal_count: proposals.length,
        scope_level: ruleScope.scope_level,
        unsupported_count: parsed.unsupported_conditions.length,
      },
    } as never);

    const scopeOut: IntentScope =
      ruleScope.scope_level === "tenant"
        ? { level: "tenant", customer_id: null }
        : { level: "customer", customer_id: ruleScope.customer_id };

    return {
      capability: "hold_creation",
      intent_id: intentId,
      title: parsed.title.slice(0, 120),
      summary: parsed.summary.slice(0, 600),
      prompt: data.prompt,
      scope: scopeOut,
      customer_label,
      proposals,
      composed_preview,
      assumptions: parsed.assumptions,
      rejected: parsed.rejected,
      unsupported_conditions: parsed.unsupported_conditions,
    };
  });

async function composeHoldCreationPreview(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase: any,
  tenantId: string,
  scope: RuleScope,
  proposals: ProposedCreationRule[],
): Promise<CreationComposedPreview> {
  const per_rule: CreationComposedPreview["per_rule"] = [];

  // Bounded order sample — same scope filter the rule would see at import.
  let q = supabase
    .from("orders")
    .select("id, order_number, customer_id, created_at")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false })
    .limit(500);
  if (scope.scope_level === "customer") q = q.eq("customer_id", scope.customer_id);
  const { data: orders } = await q;

  // Build context once per order — reused across all proposals.
  const contexts: Array<{ id: string; label: string; ctx: Awaited<ReturnType<typeof buildOrderContext>> }> = [];
  for (const o of orders ?? []) {
    const ctx = await buildOrderContext(supabase, "order.imported", o.id);
    contexts.push({ id: o.id, label: o.order_number ?? o.id.slice(0, 8), ctx });
  }

  for (const p of proposals) {
    const examples: CreationComposedPreview["per_rule"][number]["examples"] = [];
    let matches = 0;
    let evaluated = 0;
    for (const c of contexts) {
      if (!c.ctx) continue;
      evaluated++;
      if (evaluate(p.condition_tree, c.ctx)) {
        matches++;
        if (examples.length < 5) {
          examples.push({
            id: c.id, label: c.label, reason: p.actions.map((a) => a.kind).join(", "),
            href: `/app/orders/${c.id}`,
          });
        }
      }
    }
    per_rule.push({ rule_name: p.name, sample_size: evaluated, matching_in_sample: matches, examples });
  }

    return {
      per_rule,
      footnotes: [
        `Sampled the most recent ${contexts.length} orders${scope.scope_level === "customer" ? " for the selected customer" : ""}.`,
        "Preview uses the same evaluator and order context that will run at import.",
      ],
    };
}

/* ── classifyPolicyIntent ─────────────────────────────────────────────── */

/**
 * Lightweight capability classifier for the global, intent-first Create Policy
 * surface. The operator should never have to pick a capability before
 * describing intent; this classifier routes the prompt to the right compiler.
 *
 * This is intentionally a thin wrapper over the existing capability-specific
 * propose functions — it does not duplicate their logic.
 */
/**
 * Capability keys the classifier may return. Only `hold_release` and
 * `hold_creation` have compilers today — every other value signals a
 * capability gap detected BEFORE compilation, so the operator gets a
 * message that names the correct capability instead of being silently
 * routed to the wrong one.
 */
export type ClassifiedPolicyCapability =
  | "hold_release"
  | "hold_creation"
  | "wave"
  | "routing"
  | "replenishment"
  | "inventory_planning"
  | "delivery_commitment"
  | "receiving"
  | "packing"
  | "slotting"
  | "cycle_count"
  | "labor"
  | "unknown";

const CLASSIFIED_CAPABILITIES: ClassifiedPolicyCapability[] = [
  "hold_release", "hold_creation", "wave", "routing", "replenishment",
  "inventory_planning", "delivery_commitment", "receiving", "packing",
  "slotting", "cycle_count", "labor", "unknown",
];

export const classifyPolicyIntent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      tenantId: z.string().uuid(),
      prompt: z.string().min(4).max(2000),
    }).parse(d),
  )
  .handler(async ({ data }): Promise<ClassifiedPolicyCapability> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) {
      // Fallback when AI is not configured. Hold creation is the broader
      // domain (placing holds based on order/customer attributes), so default
      // to it rather than the narrower release domain.
      return "hold_creation";
    }

    const systemPrompt = [
      "You are a policy capability classifier for CairnOS.",
      "Read the operator's plain-English intent and decide which warehouse capability it belongs to.",
      "Pick the capability that OWNS the decision being described — not a capability whose name is incidentally mentioned.",
      "",
      "Capabilities:",
      "- hold_release: WHEN EXISTING ORDER HOLDS SHOULD AUTOMATICALLY RELEASE / CLEAR.",
      "  Examples: auto-release credit holds after 30 minutes; clear fraud holds when score drops below X.",
      "- hold_creation: PLACING an order on hold or requiring approval BEFORE fulfillment.",
      "  Examples: hold orders over $1,000 for manager approval; hold hazmat SKUs until safety review.",
      "- wave: WAVE PLANNING — grouping/releasing waves of work to the floor (cadence, scheduling, batching).",
      "  Examples: release waves at 8am/10:30am/2pm; build a wave every 30 minutes; prioritize same-day in waves.",
      "- routing: CARRIER / SERVICE-LEVEL selection at rate shop or label generation.",
      "  Examples: route fragile orders through UPS Ground; prefer USPS for orders under 1 lb.",
      "- replenishment: MOVING inventory from reserve into pick locations.",
      "  Examples: replenish from reserve when pick on-hand < 20; top up bins each morning.",
      "- inventory_planning: BUYING / REORDERING from suppliers (PO / ASN recommendations).",
      "  Examples: reorder when on-hand < 14 days of supply; suggest POs for slow movers monthly.",
      "- delivery_commitment: PROMISED delivery / arrival dates and SLA exception handling.",
      "- receiving: ASN receiving, putaway, and inbound quality rules.",
      "- packing: pack-out instructions, cartonization, packing-station behavior.",
      "- slotting: pick-face slotting / re-slotting rules.",
      "- cycle_count: cycle count cadence, triggers, and tolerance rules.",
      "- labor: labor / task assignment and staffing rules.",
      "- unknown: the intent doesn't clearly belong to any of the above.",
      "",
      "Rules:",
      "- Choose by the OPERATIONAL ACTION described, not by surface keywords.",
      '  e.g. "release waves at 8am" is `wave` (releasing waves), NOT `hold_release`.',
      "- If the intent mixes capabilities, classify by the PRIMARY action.",
      "- Use `unknown` only if no capability fits; do NOT force-fit into holds.",
      "- Respond only via the function tool.",
    ].join("\n");

    const resp = await fetch(LOVABLE_AI_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: DEFAULT_MODEL,
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: data.prompt },
        ],
        tools: [{
          type: "function",
          function: {
            name: "classify_policy_capability",
            description: "Classify the operator's intent into the correct policy capability.",
            parameters: {
              type: "object",
              properties: {
                capability: {
                  type: "string",
                  enum: CLASSIFIED_CAPABILITIES,
                  description: "The capability that should compile this intent.",
                },
                reasoning: {
                  type: "string",
                  maxLength: 200,
                  description: "One-sentence reasoning for the classification.",
                },
              },
              required: ["capability"],
            },
          },
        }],
        tool_choice: { type: "function", function: { name: "classify_policy_capability" } },
      }),
    });

    if (!resp.ok) {
      // Degrade gracefully to hold_creation on AI errors.
      return "hold_creation";
    }

    const body = await resp.json();
    const toolCall = body?.choices?.[0]?.message?.tool_calls?.[0];
    const args = toolCall?.function?.arguments;
    if (!args) return "hold_creation";

    try {
      const parsed = JSON.parse(args as string);
      if (CLASSIFIED_CAPABILITIES.includes(parsed.capability)) {
        return parsed.capability as ClassifiedPolicyCapability;
      }
    } catch {
      // ignore parse errors; fall through
    }
    return "hold_creation";
  });
